import React, { useState } from 'react';
import { message } from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    CopyOutlined
} from "@ant-design/icons";
import CommonTable from '../../../global/table';
import CommonCardView from '../../../global/cardView';
import NoRecordFound from '../../../global/noRecordFound';
import CommonDialog from '../../../global/dialog';
import CommonDrawer from '../../../global/drawer';
import { useQueryClient } from '@tanstack/react-query';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import EnhancedAutorespondersGridView from './EnhancedAutorespondersGridView';
import EnhancedAutorespondersDetails from './enhancedAutorespondersDetails';

const EnhancedAutorespondersList = ({
    automations,
    sort,
    setSort,
    isLoading,
    viewType,
    setEditAutomation,
    keyParams
}) => {
    const [toggleModal, setToggleModal] = useState({
        show: false,
        type: "",
    });
    const [detailsDrawer, setDetailsDrawer] = useState({
        show: false,
        data: null
    });
    const [deleteId, setDeleteId] = useState(null);
    const [cloneId, setCloneId] = useState(null);
    const queryClient = useQueryClient();

    // Mutations
    const { mutate: cloneAutoresponder, isPending: cloneMutationPending } = useGenericMutation({
        endpoint: "autoresponder/clone",
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.autoresponders.lists(keyParams),
                type: "active"
            });
            setToggleModal({ show: false, type: "" });
            setCloneId(null);
        },
        successMessage: "Autoresponder cloned successfully"
    });

    const { mutate: deleteAutoresponder, isPending: deleteMutationPending } = useGenericMutation({
        endpoint: "autoresponder/delete",
        method: "POST",
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.autoresponders.lists(keyParams),
                type: "active"
            });
            setToggleModal({ show: false, type: "" });
            setDeleteId(null);
        },
        successMessage: "Autoresponder deleted successfully"
    });

    const handleEdit = (id) => {
        setEditAutomation({ show: true, id });
    };

    const handleDetails = (record) => {
        setDetailsDrawer({ show: true, data: record });
    };

    const handleClone = () => {
        cloneAutoresponder({ id: cloneId });
    };

    const handleDelete = () => {
        deleteAutoresponder({ id: deleteId });
    };

    const columns = [
        {
            title: "Internal Autoresponse Name",
            dataIndex: "name",
            key: "name",
            sortType: "name",
            render: (text, record) => (
                <p className="text-primary cursor-pointer hover:underline">
                    {text}
                </p>
            ),
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            sortType: "title"
        },
        {
            title: "Message",
            dataIndex: "message",
            key: "message",
            render: (text) => (
                <div
                    className="max-w-xs truncate"
                    dangerouslySetInnerHTML={{
                        __html: text.length > 50 ? text.substring(0, 50) + "..." : text
                    }}
                />
            )
        }
    ];

    const data = Array.isArray(automations) && automations.length > 0
        ? automations.map((item) => ({
            ...item,
            key: item._id,
            id: item._id,
        }))
        : [];

    const actions = [
        {
            label: "Clone",
            icon: <CopyOutlined />,
            onClick: (record) => {
                setCloneId(record.id);
                setToggleModal({ show: true, type: "clone" });
            },
            className: "!text-green-500 !border-green-500 hover:!border-green-500 hover:!bg-green-500 hover:!text-white",
        },
        {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: (record) => handleEdit(record.id),
            className: "!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white",
        },
        {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: (record) => {
                setDeleteId(record.id);
                setToggleModal({ show: true, type: "delete" });
            },
            className: "!text-red-500 !border-red-500 hover:!border-red-500 hover:!bg-red-500 hover:!text-white",
        }
    ];

    return (
        <>
            {data.length > 0 ? (
                <>
                    {viewType === "LIST" ? (
                        <CommonTable
                            columns={columns}
                            data={data}
                            showActions={true}
                            actions={actions}
                            sort={sort}
                            setSort={setSort}
                            onRowClick={(record) => handleDetails(record)}
                        />
                    ) : (
                        <CommonCardView>
                            {data.map((record) => (
                                <EnhancedAutorespondersGridView
                                    key={record.id}
                                    record={record}
                                    actions={actions}
                                    onCardClick={() => handleDetails(record)}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            ) : (
                <NoRecordFound message="No autoresponders found." />
            )}

            {detailsDrawer.show && (
                <CommonDrawer
                    open={detailsDrawer.show}
                    setOpen={(val) => setDetailsDrawer(prev => ({ ...prev, show: val }))}
                    title="Autoresponder Details"
                    width="700px"
                >
                    <EnhancedAutorespondersDetails data={detailsDrawer.data} />
                </CommonDrawer>
            )}

            {toggleModal.show && (
                <CommonDialog
                    title={toggleModal.type === "delete" ? "Delete Autoresponder" : "Clone Autoresponder"}
                    description={toggleModal.type === "delete"
                        ? "Are you sure you want to delete this Autoresponder?"
                        : "Are you sure you want to clone this Autoresponder?"}
                    open={toggleModal.show}
                    setOpen={() => setToggleModal({ show: false, type: "" })}
                    onOk={toggleModal.type === "delete" ? handleDelete : handleClone}
                    onCancel={() => setToggleModal({ show: false, type: "" })}
                    isConfirmButtonLoading={toggleModal.type === "delete" ? deleteMutationPending : cloneMutationPending}
                    okButtonTitle={toggleModal.type === "delete" ? "Delete" : "Clone"}
                />
            )}
        </>
    );
};

export default EnhancedAutorespondersList;
